import { HasId } from 'common/table/has-id.interface';
import { inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  ConfirmComponent,
  ConfirmDialogAction,
  ConfirmDialogData,
} from 'common/common/confirm/confirm.component';
import { firstValueFrom } from 'rxjs';

export type ActionScope = 'toolbar' | 'contextMenu' | 'menu';

export interface Action<T> {
  id: string;
  icon: string;
  scopes: ActionScope[];
  labelMessageKey: string;
  disabled(a?: T): boolean;
  on(a?: T): Promise<void>;
}

export interface MultiSelectionAction<T> extends Action<T> {
  onMany(a: T[]): Promise<void>;
}

export function supportsMultiSelect<T>(
  action: Action<T> | null,
): action is MultiSelectionAction<T> {
  return (
    action !== null &&
    action !== undefined &&
    'onMany' in action &&
    typeof action.onMany === 'function'
  );
}

export interface EditingSupport<T> {
  getActions(): Action<T>[];
}

export abstract class BasicEditingSupport<T extends HasId> implements EditingSupport<T> {
  protected route = inject(ActivatedRoute);
  protected router = inject(Router);
  protected dialog = inject(MatDialog);

  protected DELETE_ACTION: MultiSelectionAction<T> = {
    id: 'delete',
    scopes: ['menu', 'contextMenu'],
    icon: 'delete',
    labelMessageKey: 'general.delete',
    disabled: () => {
      return false;
    },
    on: async (item: T) => {
      const dialogRef = await this.openConfirmDialog([item], ConfirmDialogAction.DELETE);
      const isConfirmed = await firstValueFrom(dialogRef.afterClosed());
      if (isConfirmed) {
        await this.deleteByIds([item.id]);
      }
    },
    onMany: async (items: T[]) => {
      const dialogRef = await this.openConfirmDialog(items, ConfirmDialogAction.DELETE);
      const isConfirmed = await firstValueFrom(dialogRef.afterClosed());
      if (isConfirmed) {
        await this.deleteByIds(items.map((item) => item.id));
      }
    },
  };

  getActions(): Action<T>[] {
    return [this.DELETE_ACTION];
  }

  abstract deleteByIds(ids: number[]): Promise<void>;

  protected async addItem() {
    await this.router.navigate(['add'], { relativeTo: this.route.parent });
  }

  private async openConfirmDialog(
    items: T[],
    confirmDialogAction: ConfirmDialogAction,
  ): Promise<MatDialogRef<ConfirmComponent, boolean>> {
    return this.dialog.open<ConfirmComponent, ConfirmDialogData, boolean>(ConfirmComponent, {
      data: {
        confirmDialogAction: confirmDialogAction,
        items: items.map((item) => this.getItemDisplayName(item)),
      },
    });
  }

  protected getItemDisplayName(item: T): string {
    return String(item.id);
  }
}
